// core/react/celebrate.js — a short confetti burst for a moment worth celebrating:
// a purchase, a renewal, a trial. One canvas over the page for about two and a half
// seconds, then it removes itself. Nothing loads from the network, and it's skipped
// when the viewer prefers reduced motion.

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Fire the confetti. colors: CSS colours (defaults include the page's --accent). */
export function celebrate({ colors, count = 150, duration = 2600 } = {}) {
  if (typeof document === 'undefined' || reducedMotion()) return
  const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim()
  const palette = colors || [accent || '#7a1f3d', '#f0b429', '#16a34a', '#2f7bf5', '#ff3b5c', '#ffffff']
  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '9999' })
  document.body.appendChild(canvas)
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const W = window.innerWidth, H = window.innerHeight
  canvas.width = W * dpr; canvas.height = H * dpr
  const g = canvas.getContext('2d')
  if (!g) { canvas.remove(); return }
  g.scale(dpr, dpr)

  // Two cannons from the bottom corners, aimed up and inward, plus a soft burst from the top.
  const parts = []
  const scale = Math.max(0.7, Math.min(1.2, W / 900))
  for (let i = 0; i < count; i++) {
    const side = i % 3
    let x, y, a, v
    if (side === 2) { x = W * (0.3 + Math.random() * 0.4); y = -10; a = Math.PI / 2 + (Math.random() - 0.5) * 0.9; v = 2 + Math.random() * 4 }
    else { const left = side === 0; x = left ? -10 : W + 10; y = H * 0.85; a = left ? -Math.PI / 2 + 0.35 + Math.random() * 0.55 : -Math.PI / 2 - 0.35 - Math.random() * 0.55; v = (11 + Math.random() * 9) * scale }
    parts.push({
      x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
      w: 6 + Math.random() * 6, h: 4 + Math.random() * 8, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
      tilt: Math.random() * Math.PI, vt: 0.05 + Math.random() * 0.1,
      c: palette[i % palette.length], round: Math.random() < 0.25, delay: side === 2 ? 200 + Math.random() * 300 : Math.random() * 120,
    })
  }
  const t0 = performance.now()
  let last = t0
  const frame = (now) => {
    const t = now - t0, dt = Math.min(2, (now - last) / 16.7)
    last = now
    g.clearRect(0, 0, W, H)
    const fade = t > duration * 0.7 ? Math.max(0, 1 - (t - duration * 0.7) / (duration * 0.3)) : 1
    for (const p of parts) {
      if (t < p.delay) continue
      p.vy += 0.32 * dt; p.vx *= 0.99; p.vy *= 0.99
      p.x += p.vx * dt; p.y += p.vy * dt
      p.r += p.vr * dt; p.tilt += p.vt * dt
      g.save()
      g.globalAlpha = fade
      g.translate(p.x, p.y); g.rotate(p.r)
      g.fillStyle = p.c
      if (p.round) { g.beginPath(); g.arc(0, 0, p.w / 2.4, 0, Math.PI * 2); g.fill() }
      else g.fillRect(-p.w / 2, (-p.h / 2) * Math.abs(Math.cos(p.tilt)), p.w, p.h * Math.abs(Math.cos(p.tilt)))
      g.restore()
    }
    if (t < duration) requestAnimationFrame(frame)
    else canvas.remove()
  }
  requestAnimationFrame(frame)
}
